import { useAuth } from '@/contexts/AuthContext';
import { buildForecast } from '@/lib/cashflow-forecast';
import { formatAUD } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

export function CashFlowForecast() {
  const { data } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const forecast = useMemo(
    () => buildForecast(data.transactions, data.bills),
    [data.transactions, data.bills]
  );

  // Find min cumulative point (potential cash squeeze)
  const minDay = useMemo(() => {
    let min = forecast.days[0];
    for (const d of forecast.days) {
      if (d.cumulative < min.cumulative) min = d;
    }
    return min;
  }, [forecast.days]);

  // Days with bill labels for the preview
  const billDays = forecast.days.filter(d => d.label).slice(0, 3);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">30-Day Cash Flow Forecast</h3>
          <button
            onClick={() => navigate('/more/cash-flow')}
            className="text-xs text-primary font-medium"
          >
            View Full →
          </button>
        </div>

        {/* Mini bar chart */}
        <div className="flex items-end gap-px h-12 mb-3">
          {forecast.days.map((d, i) => {
            const maxAbs = Math.max(
              ...forecast.days.map(fd => Math.abs(fd.cumulative)),
              1
            );
            const height = Math.max(Math.abs(d.cumulative) / maxAbs * 100, 4);
            const isNeg = d.cumulative < 0;
            return (
              <div
                key={i}
                className={cn(
                  'flex-1 rounded-t-sm min-w-0',
                  isNeg ? 'bg-destructive/60' : 'bg-success/60',
                  d.label && 'ring-1 ring-accent'
                )}
                style={{ height: `${height}%` }}
                title={`${d.date}: ${formatAUD(d.cumulative)}${d.label ? ` (${d.label})` : ''}`}
              />
            );
          })}
        </div>

        {/* Summary row */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-xs text-muted-foreground">Income</p>
            <p className="text-sm font-semibold text-success">{formatAUD(forecast.totalIncome)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Expenses</p>
            <p className="text-sm font-semibold text-destructive">{formatAUD(forecast.totalExpenses)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Net</p>
            <p className={cn('text-sm font-semibold', forecast.netChange >= 0 ? 'text-success' : 'text-destructive')}>
              {formatAUD(forecast.netChange)}
            </p>
          </div>
        </div>

        {/* Warning if negative dip */}
        {minDay.cumulative < -50 && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2">
            <TrendingDown className="h-4 w-4 text-destructive shrink-0" />
            <p className="text-xs text-destructive">
              Potential squeeze around {new Date(minDay.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
              {minDay.label ? ` (${minDay.label})` : ''}
            </p>
          </div>
        )}

        {/* Expanded: upcoming bills in forecast */}
        {expanded && billDays.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Upcoming bills in forecast</p>
            {billDays.map((d, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-foreground">{d.label}</span>
                <span className="text-muted-foreground">
                  {new Date(d.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                </span>
              </div>
            ))}
          </div>
        )}

        {expanded && (
          <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
            <ArrowRight className="h-3 w-3" />
            Based on your last 60 days of activity
          </div>
        )}
      </CardContent>
    </Card>
  );
}
